import moment, { type Moment } from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from '../types';
import { getDaysInMonthCalendar } from 'venn-utils';

type UseCalendarProps = {
  /**
   * UTC Date represented as a Unix Timestamp
   */
  value: number | undefined;
  /**
   * Maximum date range
   */
  range: DateRange | undefined;
  /**
   * Force 6 rows in the calendar (so two calendars side-by-side are
   * certain to display 6 rows each)
   */
  long: boolean | undefined;
  /**
   * Triggered on user selecting a date. Returns a UTC Unix Timestamp
   */
  onChange: (date: number) => void;
};

export const useCalendar = ({ value, long, onChange, range }: UseCalendarProps) => {
  const valueDate = useMemo(() => moment.utc(value).startOf('day'), [value]);
  const [visibleMonth, setVisibleMonth] = useState(valueDate.clone().startOf('month'));
  const cells: moment.Moment[][] = useMemo(() => getDaysInMonthCalendar(visibleMonth, long), [visibleMonth, long]);
  const handlePreviousMonth = useCallback(() => {
    setVisibleMonth(visibleMonth.clone().add(-1, 'month'));
  }, [visibleMonth]);
  const handleNextMonth = useCallback(() => {
    setVisibleMonth(visibleMonth.clone().add(1, 'month'));
  }, [visibleMonth]);
  const handleSelect = useCallback(
    (date: Moment) => {
      onChange(date.valueOf());
      if (date.month() !== visibleMonth.month()) {
        setVisibleMonth(date.clone().startOf('month'));
      }
    },
    [onChange, visibleMonth],
  );
  useEffect(() => {
    setVisibleMonth(valueDate.clone().startOf('month'));
  }, [valueDate]);
  const isDisabled = (date: moment.Moment) => {
    if (range) {
      if (!range.to || date.valueOf() > range.to || !range.from || date.valueOf() < range.from) {
        return true;
      }
    }

    return false;
  };

  return {
    cells,
    visibleMonth,
    handlePreviousMonth,
    handleNextMonth,
    handleSelect,
    isDisabled,
    valueDate,
  };
};
